import { Inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';

import { ApiService } from './api.service';
import { ApiUrl } from './constants';
import { RequestParams } from '@src/app/shared/types/common';
import { Router } from '@angular/router';
import { RoutesEnum } from '@src/app/enums';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  private currentLoanTypeSubject = new BehaviorSubject<
    Record<string, any> | undefined
  >(undefined);
  currentLoanType$: Observable<Record<string, any> | undefined> =
    this.currentLoanTypeSubject.asObservable();

  private loanTypesSubject = new BehaviorSubject<any>(undefined);
  loanTypes$: Observable<any> = this.loanTypesSubject.asObservable();

  private currentCardTypeSubject = new BehaviorSubject<any>(0);
  currentCardType$: Observable<any> =
    this.currentCardTypeSubject.asObservable();

  private currentDigitalIdSubject = new BehaviorSubject<number | undefined>(
    undefined,
  );
  currentDigitalId$: Observable<number | undefined> =
    this.currentDigitalIdSubject.asObservable();

  constructor(
    @Inject(Router) private router: Router,
    private apiService: ApiService,
  ) {}

  get currentLoanType(): Record<string, any> | undefined {
    return this.currentLoanTypeSubject.value;
  }

  get currentCardType(): number {
    return this.currentCardTypeSubject.value;
  }

  get currentDigitalId(): number | undefined {
    return this.currentDigitalIdSubject.value;
  }

  get loanTypes(): any {
    return this.currentLoanTypeSubject.value;
  }

  setIsLoading(value: boolean): void {
    this.isLoadingSubject.next(value);
  }

  setCurrentLoanType(type: Record<string, any> | undefined): void {
    this.currentLoanTypeSubject.next(type);
  }

  setLoanTypes(items: any): void {
    this.loanTypesSubject.next(items);
  }

  setCurrentCardType(id: number | undefined): void {
    this.currentCardTypeSubject.next(id);
  }

  setCurrentDigitalId(id: number | undefined): void {
    this.currentDigitalIdSubject.next(id);
  }

  /**
   * @description Handling API call errors
   * @param {HttpErrorResponse} error
   */
  handleError(error: HttpErrorResponse): void {
    this.isLoadingSubject.next(false);

    if (
      error.error?.statusCode === 404 ||
      error.error?.status === 404 ||
      error?.status === 404
    ) {
      this.router.navigate(['/' + RoutesEnum.NOT_FOUND], {
        skipLocationChange: true,
      });
    } else if (error.error?.message) {
      console.error(error.error?.message);
    }
  }

  getLoansTypes(params: RequestParams): Observable<any[]> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getLoansTypes,
      params,
    });
  }

  getLoansAll(
    params: RequestParams,
    options?: { withLoading?: boolean },
  ): Observable<any[]> {
    if (options?.withLoading) {
      this.isLoadingSubject.next(true);
    }

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getLoansAll,
      params,
    });
  }

  getLoansConstructors(
    slug: number | string,
    params: RequestParams,
    options?: { withLoading?: boolean },
  ): Observable<any[]> {
    if (options?.withLoading) {
      this.isLoadingSubject.next(true);
    }

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getLoansConstructors(slug),
      params,
    });
  }

  getDepositsAll(params: RequestParams): Observable<any[]> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getDepositsAll,
      params,
    });
  }

  getDepositsIndividualAll(
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getDepositsInsividualAll,
      params,
    });
  }

  getAccountsAll(params: RequestParams): Observable<any[]> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getAccountsAll,
      params,
    });
  }

  getPackagesAll(params: RequestParams): Observable<any[]> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getPackagesAll,
      params,
    });
  }

  getCardsTypes(params: RequestParams): Observable<any[]> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getCardsTypes,
      params,
    });
  }

  getCardsAll(
    params: RequestParams,
    options?: { withLoading?: boolean },
  ): Observable<Record<string, any>> {
    if (options?.withLoading) {
      this.isLoadingSubject.next(true);
    }

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getCardsAll,
      params,
    });
  }

  getCardsFilters(params: RequestParams): Observable<any> {
    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getCardsFilters,
      params,
    });
  }

  getLaonBySlug(slug: number | string, params: RequestParams): Observable<any> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getLoan(slug),
      params,
    });
  }

  getDepositBySlug(
    slug: number | string,
    params: RequestParams,
  ): Observable<any> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getDeposit(slug),
      params,
    });
  }

  getCardBySlug(slug: number | string, params: RequestParams): Observable<any> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getCard(slug),
      params,
    });
  }

  getAccountBySlug(
    slug: number | string,
    params: RequestParams,
  ): Observable<any> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getAccount(slug),
      params,
    });
  }

  getPackageBySlug(
    slug: number | string,
    params: RequestParams,
  ): Observable<any> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getPackage(slug),
      params,
    });
  }

  getDigitalBankingPage(
    id: number,
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getProductsDigitalBanking(id),
      params,
    });
  }
}
